/**
 * Обработчик формы «Оставить заявку» — реальная отправка на сервер.
 *
 * Контракт (apps.common.views.request_submit):
 *   - 200 {ok: true}                — письмо ушло, показать «Спасибо»
 *   - 422 {ok: false, errors}       — серверная валидация; прокидываем ошибки
 *                                     к полям, оставляем введённые данные
 *   - 429 {ok: false, detail}       — превышен лимит попыток
 *   - 503 {ok: false, detail}       — приём заявки недоступен
 *
 * Состояния формы:
 *   - idle (по умолчанию)
 *   - submitting (блокируем кнопку, показываем aria-busy)
 *   - ok (скрываем форму, показываем success-блок)
 *
 * Никаких глобальных переменных: каждая форма изолирована замыканием.
 * Поля с маской (``data-request-form-mask="phone"``) подключает
 * ``bindPhoneMask`` — живое форматирование телефона ``+7 (999) 999-99-99``.
 */

import { bindPhoneMask } from './phone-mask.js';

const SUCCESS_HIDDEN_ATTR = 'hidden';
const ERROR_ROLES = ['alert'];

function extractErrorMessage(messages) {
  /* Сервер отдаёт список ``[{"message": "...", "code": "..."}]`` — берём
     первый ``message``. На случай строки (старый/нестандартный бэкенд)
     возвращаем её как есть. */
  if (Array.isArray(messages) && messages.length > 0) {
    const first = messages[0];
    if (first && typeof first === 'object' && 'message' in first) {
      return String(first.message);
    }
    return String(first);
  }
  return String(messages);
}

function getCsrfToken(form) {
  const field = form.querySelector('input[name="csrfmiddlewaretoken"]');
  if (field && field.value) return field.value;
  const match = document.cookie.match(/(?:^|;\s*)csrftoken=([^;]+)/);
  return match ? decodeURIComponent(match[1]) : '';
}

function findField(form, name) {
  return form.querySelector(`[data-request-form-field="${name}"]`);
}

function findErrorSlot(form, name) {
  return form.querySelector(`[data-request-form-error="${name}"]`);
}

function findStatusSlot(form) {
  return form.parentElement?.querySelector('[data-request-form-status]')
    || form.querySelector('[data-request-form-status]')
    || null;
}

function clearFieldError(form, name) {
  const field = findField(form, name);
  const slot = findErrorSlot(form, name);
  if (field) field.setAttribute('aria-invalid', 'false');
  if (slot) {
    slot.hidden = true;
    slot.textContent = slot.textContent || '';
    /* Возвращаем дефолтный текст подсказки, чтобы при следующей валидации
       placeholder был осмысленным (бэкенд может прислать иной текст). */
  }
}

function clearAllErrors(form) {
  form.querySelectorAll('[data-request-form-error]').forEach((slot) => {
    slot.hidden = true;
  });
  form.querySelectorAll('[data-request-form-field]').forEach((field) => {
    field.setAttribute('aria-invalid', 'false');
  });
  const status = findStatusSlot(form);
  if (status) {
    status.hidden = true;
    status.textContent = '';
  }
}

function setFieldError(form, name, message) {
  const field = findField(form, name);
  const slot = findErrorSlot(form, name);
  if (slot) {
    slot.textContent = message || slot.textContent || 'Поле заполнено неверно.';
    slot.hidden = false;
  }
  if (field) {
    field.setAttribute('aria-invalid', 'true');
    const describedBy = field.getAttribute('aria-describedby');
    if (describedBy && slot && slot.id && !describedBy.split(/\s+/).includes(slot.id)) {
      field.setAttribute('aria-describedby', `${describedBy} ${slot.id}`.trim());
    }
  }
}

function setStatus(form, message) {
  const status = findStatusSlot(form);
  if (!status) return;
  status.textContent = message;
  status.hidden = !message;
}

function focusFirstInvalid(form, errors) {
  const names = Object.keys(errors || {});
  for (const name of names) {
    const field = findField(form, name);
    if (field) { field.focus(); return; }
  }
  const firstField = form.querySelector('[data-request-form-field]');
  firstField?.focus();
}

function showSuccess(form) {
  form.hidden = true;
  form.setAttribute('aria-hidden', 'true');
  const wrapper = form.parentElement;
  const success = wrapper?.querySelector('[data-request-form-success]');
  if (success) {
    success.hidden = false;
    success.setAttribute('aria-live', 'polite');
    success.focus();
  }
}

function showForm(form) {
  const wrapper = form.parentElement;
  const success = wrapper?.querySelector('[data-request-form-success]');
  if (success) success.hidden = true;
  form.hidden = false;
  form.removeAttribute('aria-hidden');
}

function setSubmitting(form, submitting) {
  const button = form.querySelector('[data-request-form-submit]');
  if (!button) return;
  button.disabled = submitting;
  button.setAttribute('aria-busy', submitting ? 'true' : 'false');
  form.setAttribute('aria-busy', submitting ? 'true' : 'false');
}

async function postForm(form) {
  const csrfToken = getCsrfToken(form);
  const body = new FormData(form);
  const response = await fetch(form.action || '/request/', {
    method: 'POST',
    body,
    credentials: 'same-origin',
    headers: {
      'Accept': 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
      'X-CSRFToken': csrfToken,
    },
  });
  let payload = null;
  const text = await response.text();
  if (text) {
    try { payload = JSON.parse(text); }
    catch { payload = null; }
  }
  return { ok: response.ok, status: response.status, payload, raw: text };
}

function buildClientErrors(form) {
  const errors = {};
  const name = form.querySelector('[data-request-form-field="name"]');
  if (name && !name.value.trim()) errors.name = ['Укажите имя.'];
  const phone = form.querySelector('[data-request-form-field="phone"]');
  if (phone && !phone.value.trim()) errors.phone = ['Укажите телефон.'];
  const consent = form.querySelector('[data-request-form-field="consent"]');
  if (consent && !consent.checked) errors.consent = ['Для отправки нужно согласие.'];
  return errors;
}

export function initRequestForm(root = document) {
  /* Маска телефона — независимо от submit-логики и идемпотентно. */
  root.querySelectorAll('[data-request-form-mask="phone"]').forEach(bindPhoneMask);

  root.querySelectorAll('[data-request-form]').forEach((form) => {
    if (form.dataset.requestFormBound === 'true') return;
    form.dataset.requestFormBound = 'true';

    /* Сбрасываем подсветку ошибок при правке поля. */
    form.querySelectorAll('[data-request-form-field]').forEach((field) => {
      const name = field.getAttribute('data-request-form-field');
      const handler = () => clearFieldError(form, name);
      field.addEventListener('input', handler);
      field.addEventListener('change', handler);
    });

    /* Reset: возвращаем форму в idle. */
    const wrapper = form.parentElement;
    const reset = wrapper?.querySelector('[data-request-form-reset]');
    reset?.addEventListener('click', () => {
      form.reset();
      clearAllErrors(form);
      showForm(form);
      const first = form.querySelector('[data-request-form-field]');
      first?.focus();
    });

    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      if (form.dataset.requestFormSubmitting === 'true') return;
      clearAllErrors(form);

      /* Клиентская валидация — UX, не источник истины. */
      const clientErrors = buildClientErrors(form);
      if (Object.keys(clientErrors).length > 0) {
        for (const [name, messages] of Object.entries(clientErrors)) {
          setFieldError(form, name, messages[0]);
        }
        focusFirstInvalid(form, clientErrors);
        return;
      }

      form.dataset.requestFormSubmitting = 'true';
      setSubmitting(form, true);

      try {
        const result = await postForm(form);

        if (result.status === 200 && result.payload?.ok === true) {
          showSuccess(form);
          return;
        }

        if (result.status === 422) {
          const serverErrors = (result.payload && result.payload.errors) || {};
          for (const [name, messages] of Object.entries(serverErrors)) {
            const text = extractErrorMessage(messages);
            setFieldError(form, name, text);
          }
          setStatus(form, (result.payload && result.payload.detail) || 'Проверьте поля формы.');
          focusFirstInvalid(form, serverErrors);
          return;
        }

        if (result.status === 429) {
          setStatus(form, 'Слишком много попыток. Попробуйте позже.');
          return;
        }

        if (result.status === 503) {
          setStatus(form, 'Не удалось отправить заявку. Попробуйте позже.');
          return;
        }

        /* Непредвиденный ответ: не теряем данные, держим форму. */
        setStatus(form, 'Не удалось отправить заявку. Попробуйте позже.');
      } catch (networkError) {
        setStatus(form, 'Нет связи с сервером. Попробуйте позже.');
      } finally {
        form.dataset.requestFormSubmitting = 'false';
        setSubmitting(form, false);
      }
    });
  });
}